import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { SSE } from 'sse.js';
import type { SSEvent, ReadyStateEvent } from 'sse.js';

const SSE_CLOSED = 2;

@Injectable({
  providedIn: 'root',
})
export class SseService {
  /** Creates an SSE connection via POST and emits parsed JSON events. Closes on unsubscribe. */
  public stream<T>(url: string, body: unknown, eventTypes: string[] = [], headers: Record<string, string> = {}): Observable<T> {
    return new Observable<T>((observer: Subscriber<T>) => {
      const source = new SSE(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        payload: JSON.stringify(body),
        start: false,
      });

      const handler = (event: SSEvent): void => {
        try {
          observer.next(JSON.parse(event.data) as T);
        } catch {
          // Ignore malformed JSON in SSE data field
        }
      };

      for (const type of eventTypes) {
        source.addEventListener(type, handler);
      }

      source.addEventListener('error', (event: SSEvent): void => {
        observer.error(new Error(event.data || 'SSE connection error'));
      });

      source.addEventListener('readystatechange', (event: ReadyStateEvent): void => {
        if (event.readyState === SSE_CLOSED) {
          observer.complete();
        }
      });

      source.stream();

      return (): void => source.close();
    });
  }
}
